import type { SeededRNG } from '../core/rng';
import { initNoise, simplex2D } from './noise';
import type { TerrainDrawContext } from '../sim/maskCanvas';

export interface CaveOptions {
  fillPercent: number;
  smoothPasses: number;
  minChamberSize: number;
  connectRegions: boolean;
}

export function generateCaveMap(
  width: number,
  height: number,
  rng: SeededRNG,
  options: CaveOptions,
): boolean[][] {
  const { fillPercent, smoothPasses, connectRegions } = options;

  initNoise(rng);

  let map: boolean[][] = [];
  for (let y = 0; y < height; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < width; x++) {
      const noiseVal = simplex2D(x * 0.05, y * 0.05);
      const threshold = fillPercent / 100 + noiseVal * 0.1;
      row.push(rng.next() < threshold);
    }
    map.push(row);
  }

  for (let pass = 0; pass < smoothPasses; pass++) {
    map = smoothCaveMap(map, width, height);
  }

  if (connectRegions) {
    map = connectCaveRegions(map, width, height, rng);
  }

  return map;
}

function smoothCaveMap(map: boolean[][], width: number, height: number): boolean[][] {
  const newMap: boolean[][] = [];

  for (let y = 0; y < height; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < width; x++) {
      const wallCount = countNeighborWalls(map, x, y, width, height);

      if (wallCount >= 5) {
        row.push(true);
      } else if (wallCount <= 2) {
        row.push(false);
      } else {
        row.push(map[y][x]);
      }
    }
    newMap.push(row);
  }

  return newMap;
}

function countNeighborWalls(
  map: boolean[][],
  x: number,
  y: number,
  width: number,
  height: number,
): number {
  let count = 0;

  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;

      const nx = x + dx;
      const ny = y + dy;

      if (nx < 0 || nx >= width || ny < 0 || ny >= height) {
        count++;
      } else if (map[ny][nx]) {
        count++;
      }
    }
  }

  return count;
}

function connectCaveRegions(
  map: boolean[][],
  width: number,
  height: number,
  rng: SeededRNG,
): boolean[][] {
  const regions = findRegions(map, width, height);

  if (regions.length <= 1) {
    return map;
  }

  const newMap = map.map((row) => [...row]);

  for (let i = 1; i < regions.length; i++) {
    const region1 = regions[i - 1];
    const region2 = regions[i];

    const point1 = region1[Math.floor(rng.next() * region1.length)];
    const point2 = region2[Math.floor(rng.next() * region2.length)];

    carveTunnel(newMap, point1.x, point1.y, point2.x, point2.y, width, height);
  }

  return newMap;
}

function findRegions(
  map: boolean[][],
  width: number,
  height: number,
): Array<Array<{ x: number; y: number }>> {
  const visited: boolean[][] = Array.from({ length: height }, () => Array(width).fill(false));
  const regions: Array<Array<{ x: number; y: number }>> = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!map[y][x] && !visited[y][x]) {
        const region = floodFill(map, visited, x, y, width, height);
        if (region.length > 50) {
          regions.push(region);
        }
      }
    }
  }

  return regions;
}

function floodFill(
  map: boolean[][],
  visited: boolean[][],
  startX: number,
  startY: number,
  width: number,
  height: number,
): Array<{ x: number; y: number }> {
  const region: Array<{ x: number; y: number }> = [];
  const queue: Array<{ x: number; y: number }> = [{ x: startX, y: startY }];
  visited[startY][startX] = true;

  while (queue.length > 0) {
    const { x, y } = queue.shift()!;
    region.push({ x, y });

    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (Math.abs(dx) + Math.abs(dy) !== 1) continue;

        const nx = x + dx;
        const ny = y + dy;

        if (nx >= 0 && nx < width && ny >= 0 && ny < height && !map[ny][nx] && !visited[ny][nx]) {
          visited[ny][nx] = true;
          queue.push({ x: nx, y: ny });
        }
      }
    }
  }

  return region;
}

function carveTunnel(
  map: boolean[][],
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  width: number,
  height: number,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const steps = Math.max(Math.abs(dx), Math.abs(dy));

  for (let i = 0; i <= steps; i++) {
    const t = steps === 0 ? 0 : i / steps;
    const x = Math.floor(x1 + dx * t);
    const y = Math.floor(y1 + dy * t);

    for (let r = -2; r <= 2; r++) {
      for (let c = -2; c <= 2; c++) {
        const nx = x + c;
        const ny = y + r;
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          map[ny][nx] = false;
        }
      }
    }
  }
}

export function applyCaveToCanvas(
  ctx: TerrainDrawContext,
  caveMap: boolean[][],
  cellSize: number,
  offsetX: number,
  offsetY: number,
): void {
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = '#000000';

  for (let y = 0; y < caveMap.length; y++) {
    for (let x = 0; x < caveMap[y].length; x++) {
      if (!caveMap[y][x]) {
        ctx.fillRect(offsetX + x * cellSize, offsetY + y * cellSize, cellSize, cellSize);
      }
    }
  }

  ctx.globalCompositeOperation = 'source-over';
}

export function addFormations(
  ctx: TerrainDrawContext,
  caveMap: boolean[][],
  cellSize: number,
  rng: SeededRNG,
): void {
  const height = caveMap.length;
  const width = caveMap[0].length;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (!caveMap[y][x]) {
        if (caveMap[y - 1][x] && rng.next() < 0.02) {
          const formationHeight = rng.nextInt(10, 30);
          const formationWidth = rng.nextInt(3, 8);
          ctx.fillStyle = '#8B4513';
          ctx.beginPath();
          ctx.moveTo(x * cellSize, y * cellSize);
          ctx.lineTo(x * cellSize - formationWidth / 2, y * cellSize + formationHeight);
          ctx.lineTo(x * cellSize + formationWidth / 2, y * cellSize + formationHeight);
          ctx.closePath();
          ctx.fill();
        }

        if (caveMap[y + 1][x] && rng.next() < 0.02) {
          const formationHeight = rng.nextInt(10, 30);
          const formationWidth = rng.nextInt(3, 8);
          ctx.fillStyle = '#8B4513';
          ctx.beginPath();
          ctx.moveTo(x * cellSize, y * cellSize);
          ctx.lineTo(x * cellSize - formationWidth / 2, y * cellSize - formationHeight);
          ctx.lineTo(x * cellSize + formationWidth / 2, y * cellSize - formationHeight);
          ctx.closePath();
          ctx.fill();
        }
      }
    }
  }
}
